import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { SelectBox, TagBox } from 'devextreme-react';

import CustomStore from 'devextreme/data/custom_store';
import { CircleSpinner } from 'react-spinners-kit';
import { Button } from '../../../../components/Button';
import master from '../../../../services/master';
import { FormGroup } from '../../../../components/FormGroup';
import { useToast } from '../../../../hooks/toast';
import api from '../../../../services/api';
import { Alert } from '../../../../components/Alert';
import { useAuth } from '../../../../hooks/auth';
import { LoadingOverlay } from './styles';

interface DialogCustomProps {
  idProfessional: number;
  open: boolean;
  handleClose(): void;
}

interface CustomStoreProps {
  store: CustomStore;
  paginate: boolean;
}

export const NewGroupDialog: React.FC<DialogCustomProps> = ({
  idProfessional,
  open,
  handleClose,
}) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [accessType, setAccessType] = useState(0);
  const [groups, setGroups] = useState<CustomStoreProps>(
    {} as CustomStoreProps,
  );

  const loadGroups = useCallback(async () => {
    setGroups({
      store: new CustomStore({
        key: 'IDGroupAD',
        loadMode: 'raw',
        load: async () => {
          const response = await master.get('/master/ad-groups');
          const professionalGroups = await master.get(
            `/master/professionals/${idProfessional}/groups`,
          );
          return response.data.filter(
            (i: any) =>
              !professionalGroups.data
                .map((x: any) => x.IDGroupAD)
                .includes(i.IDGroupAD),
          );
        },
      }),
      paginate: true,
    });
  }, [idProfessional]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleSend = useCallback(async () => {
    setLoading(true);
    if (accessType === 1) {
      // TODO incluir email + adicionar no temp numa mesma chamada
      selectedGroups.forEach(async g => {
        await master.post('/master/professional-group-ad', {
          IDProfessional: idProfessional,
          IDGroupAD: g,
        });
      });
      await api.post('/api/group-change-logs', {
        IDProfessional: idProfessional,
        IDGroups: selectedGroups.toString(),
        Action: 1,
        IsPermanent: false,
        ExecutedBy: user.userId,
      });
      addToast({
        title: 'Group(s) added successfully!',
        type: 'success',
      });
      setLoading(false);
      handleClose();
    } else {
      // TODO incluir email + adicionar no temp numa mesma chamada
      await api.post(`/api/send-email`, {
        IDProfessional: idProfessional,
        Groups: selectedGroups,
      });
      await api.post('/api/group-change-logs', {
        IDProfessional: idProfessional,
        IDGroups: selectedGroups.toString(),
        Action: 1,
        IsPermanent: true,
        ExecutedBy: user.userId,
      });
      selectedGroups.forEach(async g => {
        await master.post('/master/professional-group-ad', {
          IDProfessional: idProfessional,
          IDGroupAD: g,
        });
      });
      addToast({
        title: 'Group(s) added successfully!',
        type: 'success',
      });
      setLoading(false);
      handleClose();
    }
  }, [
    accessType,
    addToast,
    handleClose,
    idProfessional,
    selectedGroups,
    user.userId,
  ]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="form-dialog-title">Add Group(s)</DialogTitle>
      <DialogContent>
        {loading && (
          <div className="loading-overlay">
            <LoadingOverlay>
              <CircleSpinner color="#8b0304" />
            </LoadingOverlay>
          </div>
        )}
        <FormGroup fieldSetLabel="Access Type">
          <SelectBox
            dataSource={[
              { ID: 1, Name: 'Temporary' },
              { ID: 2, Name: 'Permanent' },
            ]}
            displayExpr="Name"
            valueExpr="ID"
            onValueChanged={e => setAccessType(e.value)}
            stylingMode="outlined"
          />
        </FormGroup>
        <FormGroup fieldSetLabel="Group(s)">
          <TagBox
            searchEnabled
            dataSource={groups}
            displayExpr="Name"
            valueExpr="IDGroupAD"
            onValueChanged={e => setSelectedGroups(e.value)}
            stylingMode="outlined"
          />
        </FormGroup>
        {accessType === 2 && (
          <Alert type="warning">
            DISCLAIMER: If you send this request as a Permanent access type, an
            email will be sent to support@integrationconsulting.com asking them
            to assign the groups to the professional permanently.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleClose();
          }}
        >
          Close
        </Button>
        <Button
          onClick={() => {
            handleSend();
          }}
          primary
          disabled={loading}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
